export interface Person {
  "@type": "Person";
  name: string;
  givenName: string;
  familyName: string;
  jobTitle: string;
  description: string;
  url: string;
  image: string;
  email: string;
  telephone?: string;
  address?: {
    "@type": "PostalAddress";
    addressCountry: string;
    addressLocality?: string;
  };
  sameAs: string[];
  knowsAbout: string[];
  alumniOf?: string;
  worksFor?: {
    "@type": "Organization";
    name: string;
  };
}

export interface Organization {
  "@type": "Organization";
  name: string;
  url: string;
  logo: string;
  description: string;
  founder: Person;
  foundingDate: string;
  address: {
    "@type": "PostalAddress";
    addressCountry: string;
  };
  contactPoint: {
    "@type": "ContactPoint";
    contactType: string;
    email: string;
  };
}

export interface WebSite {
  "@type": "WebSite";
  name: string;
  url: string;
  description: string;
  author: Person;
  inLanguage: string[];
  potentialAction: {
    "@type": "SearchAction";
    target: string;
    "query-input": string;
  };
}

export interface BlogPosting {
  "@type": "BlogPosting";
  headline: string;
  description: string;
  image: string;
  author: Person;
  publisher: Organization;
  datePublished: string;
  dateModified: string;
  mainEntityOfPage: {
    "@type": "WebPage";
    "@id": string;
  };
  url: string;
  wordCount: number;
  keywords: string[];
  articleSection: string;
  inLanguage: string;
}

export interface BreadcrumbList {
  "@type": "BreadcrumbList";
  itemListElement: Array<{
    "@type": "ListItem";
    position: number;
    name: string;
    item: string;
  }>;
}

export class StructuredDataGenerator {
  private primaryDomain = 'https://impelatek.fr';
  private secondaryDomain = 'https://impelatek.com';
  
  // Get current domain based on window location or default to primary
  private getCurrentDomain(): string {
    if (typeof window !== 'undefined') {
      const currentHost = window.location.hostname;
      if (currentHost.includes('impelatek.com')) {
        return this.secondaryDomain;
      }
    }
    return this.primaryDomain;
  }
  
  private getBaseUrl(): string {
    return this.getCurrentDomain();
  }
  
  generatePersonSchema(lang: string = 'en'): Person {
    const baseUrl = this.getBaseUrl();
    return {
      "@type": "Person",
      name: "Moudondi Godfried",
      givenName: "Moudondi",
      familyName: "Godfried",
      jobTitle: lang === 'fr' ? "Développeur Frontend" : "Frontend Developer",
      description: lang === 'fr' 
        ? "Développeur front-end spécialisé dans les expériences web modernes, React, TypeScript et le design responsive."
        : "Frontend developer specializing in modern web experiences, React, TypeScript, and responsive design.",
      url: baseUrl,
      image: `${baseUrl}/assets/images/profile.jpg`,
      email: "contact@impelatek.fr",
      address: {
        "@type": "PostalAddress",
        addressCountry: "FR"
      },
      sameAs: [
        "https://linkedin.com/in/moudondi-godfried",
        "https://github.com/idready",
        "https://twitter.com/impelatek"
      ],
      knowsAbout: [
        "JavaScript",
        "TypeScript",
        "React",
        "Vue.js",
        "Node.js",
        "HTML5",
        "CSS3",
        "Responsive Design",
        "UI/UX Design",
        "Web Development",
        "Frontend Development"
      ]
    };
  }

  generateOrganizationSchema(lang: string = 'en'): Organization {
    const baseUrl = this.getBaseUrl();
    return {
      "@type": "Organization",
      name: "Impelatek",
      url: baseUrl,
      logo: `${baseUrl}/assets/images/logo.png`,
      description: lang === 'fr'
        ? "Services de développement web professionnel et design d'expériences utilisateur"
        : "Professional web development services and user experience design",
      founder: this.generatePersonSchema(lang),
      foundingDate: "2023",
      address: {
        "@type": "PostalAddress",
        addressCountry: "FR"
      },
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "Customer Service",
        email: "contact@impelatek.fr"
      }
    };
  }

  generateWebSiteSchema(lang: string = 'en'): WebSite {
    const baseUrl = this.getBaseUrl();
    return {
      "@type": "WebSite",
      name: "Moudondi Godfried - Web Developer",
      url: baseUrl,
      description: lang === 'fr'
        ? "Portfolio professionnel de Moudondi Godfried, développeur front-end spécialisé dans les technologies web modernes."
        : "Professional portfolio of Moudondi Godfried, frontend developer specializing in modern web technologies.",
      author: this.generatePersonSchema(lang),
      inLanguage: ["en", "fr"],
      potentialAction: {
        "@type": "SearchAction",
        target: `${baseUrl}/search?q={search_term_string}`,
        "query-input": "required name=search_term_string"
      }
    };
  }

  generateBlogPostSchema(
    title: string,
    description: string,
    image: string,
    datePublished: string,
    dateModified: string,
    url: string,
    keywords: string[],
    wordCount: number,
    lang: string = 'en'
  ): BlogPosting {
    return {
      "@type": "BlogPosting",
      headline: title,
      description,
      image,
      author: this.generatePersonSchema(lang),
      publisher: this.generateOrganizationSchema(lang),
      datePublished,
      dateModified,
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": url
      },
      url,
      wordCount,
      keywords,
      articleSection: "Web Development",
      inLanguage: lang
    };
  }

  generateBreadcrumbSchema(breadcrumbs: Array<{ name: string; url: string }>): BreadcrumbList {
    return {
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbs.map((breadcrumb, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: breadcrumb.name,
        item: breadcrumb.url
      }))
    };
  }

  generateContextualSchema(type: 'person' | 'organization' | 'website' | 'blog', data?: any, lang: string = 'en') {
    const context = {
      "@context": "https://schema.org"
    };

    switch (type) {
      case 'person':
        return { ...context, ...this.generatePersonSchema(lang) };
      case 'organization':
        return { ...context, ...this.generateOrganizationSchema(lang) };
      case 'website':
        return { ...context, ...this.generateWebSiteSchema(lang) };
      case 'blog':
        return { ...context, ...this.generateBlogPostSchema(
          data.title,
          data.description,
          data.image,
          data.datePublished,
          data.dateModified,
          data.url,
          data.keywords,
          data.wordCount,
          lang
        ) };
      default:
        return context;
    }
  }
}

export const structuredDataGenerator = new StructuredDataGenerator();
